<template>
  <div class="dromo-import-button chemtracker-module" v-if="showDromoButton">
    <component
      :is="buttonType"
      :show-badge="false"
      :show-icon="false"
      v-bind="$attrs"
      label="Import"
      @click="startImport"
      :class="clearStyle"
      id="import-button"
    >
      <img
        id="dromo-import-help"
        :src="helpSrc"
        alt="help"
        v-if="!isGhostButton"
      />
      <template v-else>
        <img :src="uploadSrc" alt="upload icon" />
        Import
      </template>
    </component>
    <b-popover
      target="dromo-import-help"
      triggers="hover focus"
      v-if="!isGhostButton"
    >
      <template slot="title">CSV File Import</template>
      Upload a CSV file to add new chemical container records and to edit
      existing records (up to 10,000).
    </b-popover>
    <VNotificationsConfirmationModal
      title="Container Import In Progress"
      modal-id="container-confirmation"
    />
    <div class="v-modal-container">
      <b-modal
        ref="vmodal"
        centered
        hide-footer
        cancel-disabled
        ok-disabled
        no-close-on-backdrop
        hide-header
        id="vmodal-dromo"
      >
        <div class="v-modal-content">
          <header class="v-modal-header">
            <div class="header-title">
              <h1>Validation in progress, please wait.</h1>
            </div>
          </header>
          <div class="v-modal-busy">
            <raft-busy
              busy-text="It may take a few minutes to see results."
              :is-raft-styling="false"
            />
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { useNotification } from "@/composables/useNotification";
import { useDromo } from "@/composables/useDromo";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import { ref } from "vue";
import RaftGenericHelper from "@/lib/raft_generic_helper";
import { useFeatureFlag } from "@/composables/useFeatureFlag";
import VActionButton from "../raft/VActionButton.vue";
import helpIcon from "../raft/help-Outline.svg";
import uploadIcon from "../../images/upload.svg";
import VButton from "../raft/VButton.vue";
import VNotificationsConfirmationModal from "../raft/VNotificationsConfirmationModal.vue";
import RaftBusy from "../RaftBusy.vue";

export default {
  name: "DromoImportButton",
  components: {
    VActionButton,
    VButton,
    VNotificationsConfirmationModal,
    RaftBusy,
  },
  props: {
    isGhostButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      helpSrc: helpIcon,
      uploadSrc: uploadIcon,
      groupContext: "",
    };
  },
  setup() {
    const { showNotification } = useNotification();
    const { config } = useDrupalSettings();
    const { dromoApiKey, dromoEnabled, dromoSchema, fetchDromoFeature } =
      useFeatureFlag();
    const isDromoFinished = ref(false);
    const userPermissions = ref([]);
    const groupContext = ref("");

    // Store dromo composable
    const dromo = ref(null);
    const dromoResultsData = ref(null);
    const dromoResultsMetadata = ref(null);
    const dromoValidating = ref(false);

    if (config.groupContext) {
      groupContext.value = config.groupContext;
    }

    const queries = {};

    queries["rog-id"] = groupContext.value;
    if (groupContext.value) {
      queries["rog-type"] = "node";
    }
    userPermissions.value = RaftGenericHelper.fetchUserPermissions(
      ["bulk import chemical containers"],
      groupContext.value,
    );

    fetchDromoFeature().then(() => {
      if (dromoEnabled.value) {
        if (dromoApiKey.value && dromoSchema.value) {
          const {
            dromo: d,
            dromoResultsData: dData,
            dromoResultsMetadata: dMeta,
            dromoFinished: dFinished,
            isBusyValidating: dValidating,
          } = useDromo({
            dromoApiKey: dromoApiKey.value,
            dromoSchema: dromoSchema.value,
          });
          dromo.value = d;
          dromoResultsData.value = dData;
          dromoResultsMetadata.value = dMeta;
          isDromoFinished.value = dFinished;
          dromoValidating.value = dValidating;
        } else {
          if (!dromoApiKey.value) {
            showNotification(
              "error",
              "Unable to initialize import tool with empty API key.",
            );
          }
          if (!dromoSchema.value) {
            showNotification(
              "error",
              "Unable to initialize import tool with empty schema.",
            );
          }
        }
      }
    });

    return {
      dromo,
      dromoResultsData,
      dromoResultsMetadata,
      dromoEnabled,
      isDromoFinished,
      userPermissions,
      dromoValidating,
    };
  },
  methods: {
    startImport() {
      this.dromo.open();
    },
    showValidatingModal() {
      this.$refs.vmodal.show();
    },
  },
  computed: {
    buttonType() {
      return this.isGhostButton ? "VButton" : "VActionButton";
    },
    clearStyle() {
      return this.isGhostButton ? "clear xsmall" : "";
    },
    showDromoButton() {
      return (
        this.dromoEnabled &&
        this.userPermissions.includes("bulk import chemical containers")
      );
    },
  },
  watch: {
    isDromoFinished(newValue) {
      if (newValue) {
        this.$bvModal.show("container-confirmation");
      }
    },
    dromoValidating(newValue) {
      if (newValue === true) {
        this.$refs.vmodal.show();
      }
      if (newValue === false) {
        this.$refs.vmodal.hide();
      }
    },
  },
};
</script>

<style scoped>
#import-button {
  color: var(--blue-1000);
  padding: 0;
}
.v-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
<style>
#vmodal-dromo___BV_modal_outer_ {
  z-index: 2147483647 !important;
}
#vmodal-dromo #vmodal-dromo___BV_modal_content_ {
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
}
#vmodal-dromo
  #vmodal-dromo___BV_modal_content_.v-modal-busy
  .busy-spinner-wrapper {
  width: 100%;
}
#vmodal-dromo
  #vmodal-dromo___BV_modal_content_
  .v-modal-busy
  .busy-spinner-wrapper
  .loading-spinner-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
